import { legalMoves, seatTeam, cardCode } from "./setbackRules";
import { determinizeHands } from "./determinize";
import { playOutHand } from "./simulate";
import { chooseHeuristicCard } from "./rolloutPolicy";

// Card-play decision via determinized Monte Carlo search (a practical,
// simplified form of ISMCTS): repeatedly sample a concrete, hidden-info-
// consistent deal of the other three hands, then for every one of my
// currently legal cards, play it out to the end of the hand (everyone else,
// and myself for the rest of the hand, using the fast heuristic policy) and
// record the resulting point swing for my team. After enough samples,
// average point-swing per candidate card is a solid estimate of its value,
// so we play the best-scoring one.
//
// This intentionally skips building a shared UCT tree across iterations
// (full "textbook" ISMCTS) - Setback hands are short (6 tricks, small
// branching factor) and rollouts are cheap, so flat averaging over many
// independent determinizations converges quickly without the extra
// bookkeeping a growing tree needs. It can be upgraded to a real tree later
// if stronger play is wanted.
const chooseCardBySearch = ({
  mySeat,
  myHand,
  trick,
  trickSuit,
  trump,
  wonTricks,
  trickCount,
  winningBid,
  playedCardsAll,
  handSizes,
  iterations,
  mustLeadTrump,
  numPlayers = 4,
}) => {
  const legal = legalMoves(myHand, trickSuit, trump, mustLeadTrump);
  if (legal.length === 1) return legal[0];

  const iters = iterations ?? Math.max(30, Math.floor(1200 / legal.length));
  const myTeam = seatTeam(mySeat);
  const stats = legal.map((card) => ({ card, total: 0, count: 0 }));

  for (let i = 0; i < iters; i++) {
    const detHands = determinizeHands({
      mySeat,
      myHand,
      trump,
      playedCards: playedCardsAll,
      handSizes,
    });

    for (const stat of stats) {
      const targetCode = cardCode(stat.card);
      let firstMoveUsed = false;
      const policy = (seat, hand, trickState, trickSuitState, trumpState) => {
        if (seat === mySeat && !firstMoveUsed) {
          firstMoveUsed = true;
          return hand.find((c) => cardCode(c) === targetCode);
        }
        return chooseHeuristicCard(hand, trickState, trickSuitState, trumpState, seat);
      };

      const result = playOutHand({
        hands: detHands,
        trump,
        trickSuitInit: trickSuit,
        trickInit: trick,
        wonTricksInit: wonTricks,
        trickCountInit: trickCount,
        toActSeat: mySeat,
        winningBid,
        policy,
        numPlayers,
      });

      const value =
        myTeam === "teamA"
          ? result.teamAScore - result.teamBScore
          : result.teamBScore - result.teamAScore;
      stat.total += value;
      stat.count += 1;
    }
  }

  let best = stats[0];
  for (const stat of stats) {
    if (stat.total / stat.count > best.total / best.count) best = stat;
  }
  return best.card;
};

export { chooseCardBySearch };
